import { PROMO_TYPE_FIXED } from 'constants/payment'
import { getBaggageCost } from 'lib/flights'
import createSelector from 'lib/createSelector'
import {
  cartHasFlights,
  getCartAccommodationSurchargeDiff,
  getCartChangePackageDiff,
  getItemsWithSurcharges,
  getSelectedInstalment,
  getServiceFee,
  isChangePackage,
  isDepositSelected,
  isFullPaymentSelected,
  isInstalmentsSelected,
} from './cartSelectors'
import { sum } from 'lib/arrayUtils'
import { adjustForDecimal } from 'lib/mathUtils'
import generateOccupancyStringByRoom from 'lib/generateOccupancyStringByRoom'
import { ITEM_STATUS_CANCELLED, ITEM_STATUS_COMPLETED } from 'constants/cart'
import { getDepositAmountPercentage } from 'lib/depositsUtils'
import { getTransferItems } from 'selectors/bookingSidebarSelectors'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'

const FINALISED_ITEM_STATUSES = [ITEM_STATUS_COMPLETED, ITEM_STATUS_CANCELLED]

export const getInsuranceTotal = createSelector(
  (state: App.State) => state.cart.insurance.insuranceAccepted,
  (state: App.State) => state.cart.insurance.insuranceQuote,
  (accepted, quote) => (accepted && quote) ? quote.total : 0,
)

export const getTotalFlightFees = createSelector(
  (state: App.State) => getBookingFee(state),
  (state: App.State) => getAtolFee(state),
  (state: App.State) => getFlightsCreditFee(state),
  (state: App.State) => state.cart.flight.journey.serviceFee,
  (bookingFee, atolFee, flightsCreditFee, serviceFee) => (bookingFee + atolFee + flightsCreditFee + serviceFee),
)

export const getFlightsTotal = createSelector(
  (state: App.State) => cartHasFlights(state),
  (state: App.State) => state.cart.flight.journey,
  (state: App.State) => getTotalFlightFees(state),
  (hasFlights, journey, totalFlightFees) => {
    if (hasFlights) {
      if (journey.itemType === 'journeyV2') {
        return journey.departing.cost + journey.returning.cost + totalFlightFees
      }
      return journey.cost
    }
    return 0
  },
)

export const getBaggageTotal = createSelector(
  (state: App.State) => state.cart.flight.passengers,
  (state: App.State) => state.cart.flight.journey.departing,
  (state: App.State) => state.cart.flight.journey.returning,
  (passengers, departingJourney, returningJourney) => {
    let total = 0
    if (departingJourney.journeyKey) {
      total += getBaggageCost(passengers, departingJourney)
    }
    if (returningJourney.journeyKey) {
      total += getBaggageCost(passengers, returningJourney)
    }
    return total
  },
)

export const getSurchargeTotal = createSelector(
  (state: App.State) => getItemsWithSurcharges(state),
  (state: App.State) => state.cart.isChangeDates,
  (state: App.State) => getCartAccommodationSurchargeDiff(state.cart),
  (items, isChangeDates, surchargeDiff) => {
    if (isChangeDates) {
      return surchargeDiff
    }

    return sum(items, item => !FINALISED_ITEM_STATUSES.includes(item.status) ? item.reservation.durationSurchargeTotal : 0)
  },
)

export const getExtraGuestSurchargeTotal = createSelector(
  (state: App.State) => state.cart.items,
  (items) => sum(items, item => (!FINALISED_ITEM_STATUSES.includes(item.status) && item.reservation?.extraGuestSurcharge) ?? 0),
)

export const getExperiencesTotal = createSelector(
  (state: App.State) => state.cart.experiences.items,
  (state: App.State) => getTransferItems(state),
  (cartExperinceItems, transferItems) => {
    const experiencesPrice = sum(Object.values(cartExperinceItems).flatMap((item) => {
      return sum(item.tickets, ticket => ticket.ticket.price * ticket.count)
    }))

    const transfersPrice = sum(transferItems, transfer => transfer.option.price)

    return experiencesPrice + transfersPrice
  },
)

export const getBedbankTotal = createSelector(
  (state: App.State) => state.cart.bedbank,
  (bedbankItem) => bedbankItem.price || 0,
)

export const getPackageBaseValue = createSelector(
  (state: App.State) => state.cart.items,
  (items) => sum(items, item => item.value),
)

export const getBnblPaidTotal = createSelector(
  (state: App.State) => state.cart.order,
  (state: App.State) => state.cart.bookDatesOnly,
  (order, bookDatesOnly) => {
    return bookDatesOnly ? order.total : 0
  },
)

export const getCompletedPackageTotal = createSelector(
  (state: App.State) => state.cart.items,
  (state: App.State) => state.cart.bookDatesOnly,
  (state: App.State) => state.cart.isChangeDates,
  (items, bookDatesOnly, isChangeDates) => {
    if (bookDatesOnly || isChangeDates) return 0

    return sum(items.filter((item: App.CartItem) => item.status === ITEM_STATUS_COMPLETED), item => item.price +
      item.reservation?.durationSurchargeTotal ?? 0 +
      item.reservation?.extraGuestSurcharge ?? 0 +
      item.reservation?.propertyFees ?? 0 +
      item.reservation?.taxesAndFees ?? 0)
  },
)

export const getPackageBaseTotal = createSelector(
  (state: App.State) => state.cart.items,
  (state: App.State) => state.cart.bookDatesOnly,
  (state: App.State) => state.cart.isChangeDates,
  (items, bookDatesOnly, isChangeDates) => {
    if (bookDatesOnly || isChangeDates) return 0

    return sum(items.filter((item: App.CartItem) => item.status !== ITEM_STATUS_COMPLETED), item => item.price)
  },
)

export const getTaxesAndFeesTotal = createSelector(
  (state: App.State) => state.cart.items,
  (state: App.State) => state.cart.bookDatesOnly,
  (state: App.State) => state.cart.isChangeDates,
  (state: App.State) => isChangePackage(state),
  (items, bookDatesOnly, isChangeDates, isChangePackage): { taxesAndFeesTotal: number; propertyFeesTotal: number; } => {
    if (bookDatesOnly || isChangeDates || isChangePackage) return { taxesAndFeesTotal: 0, propertyFeesTotal: 0 }

    const taxesAndFeesTotal = sum(items.filter((item: App.CartItem) => item.status !== ITEM_STATUS_COMPLETED), item => item.reservation.taxesAndFees ?? 0)
    const propertyFeesTotal = sum(items.filter((item: App.CartItem) => item.status !== ITEM_STATUS_COMPLETED), item => item.reservation.propertyFees ?? 0)

    return { taxesAndFeesTotal, propertyFeesTotal }
  },
)

/** Note: This isn't the GRAND total because it doesn't include property fees. But it's the total the user pays to us for the package. */
export const getAccommodationTotal = createSelector(
  (state: App.State) => getPackageBaseTotal(state),
  (state: App.State) => getSurchargeTotal(state),
  (state: App.State) => getExtraGuestSurchargeTotal(state),
  (packageBaseTotal, surchargeTotal, extraGuestSurchargeTotal) => (packageBaseTotal + surchargeTotal + extraGuestSurchargeTotal),
)

export const getAccommodationTotalValue = createSelector(
  (state: App.State) => getPackageBaseValue(state),
  (state: App.State) => getSurchargeTotal(state),
  (state: App.State) => getExtraGuestSurchargeTotal(state),
  (packageBaseValue, surchargeTotal, extraGuestSurchargeTotal) => (packageBaseValue + surchargeTotal + extraGuestSurchargeTotal),
)

export const getGiftCardTotal = createSelector(
  (state: App.State) => state.cart.giftCard.price,
  (price) => price || 0,
)

export const getPaymentBalanceTotal = createSelector(
  (state: App.State) => state.cart.paymentBalance.price,
  (price) => price || 0,
)

// To be able to calculate total for a single item by
// using existing selectors
function singleCartItem(state: App.State, item: App.CartItem) {
  return {
    ...state,
    cart: {
      ...state.cart,
      items: [item],
    },
  }
}

/**
 * Similar to `getSubtotal`, but for 1 cart item, and with property fee, without:
 * - insurance
 * - flights
 * - addons
 * - etc...
 */
export const getSingleItemBaseTotalWithFeesWithoutExtras = createSelector(
  (state: App.State, item: App.CartItem) => getPackageBaseTotal(singleCartItem(state, item)),
  (state: App.State, item: App.CartItem) => getSurchargeTotal(singleCartItem(state, item)),
  (state: App.State, item: App.CartItem) => getExtraGuestSurchargeTotal(singleCartItem(state, item)),
  (state: App.State, item: App.CartItem) => item.reservation?.propertyFees ?? 0,
  (packageBaseTotal, surchargeTotal, extraGuestSurchargeTotal, propertyFees) =>
    packageBaseTotal + surchargeTotal + extraGuestSurchargeTotal + propertyFees,
)

/**
 * Similar to `getSubtotal`, but without:
 * - insurance
 * - flights
 * - addons
 * - etc...
 */
export const getBaseSubtotalWithoutExtras = createSelector(
  (state: App.State) => getPackageBaseTotal(state),
  (state: App.State) => getSurchargeTotal(state),
  (state: App.State) => getExtraGuestSurchargeTotal(state),
  (state: App.State) => getBedbankTotal(state),
  (packageBaseTotal, surchargeTotal, extraGuestSurchargeTotal, bedbankTotal) => packageBaseTotal + surchargeTotal + extraGuestSurchargeTotal + bedbankTotal,
)

export const getSubtotal = createSelector(
  (state: App.State) => getPackageBaseTotal(state),
  (state: App.State) => getSurchargeTotal(state),
  (state: App.State) => getExtraGuestSurchargeTotal(state),
  (state: App.State) => getInsuranceTotal(state),
  (state: App.State) => getFlightsTotal(state),
  (state: App.State) => getExperiencesTotal(state),
  (state: App.State) => getBaggageTotal(state),
  (state: App.State) => getGiftCardTotal(state),
  (state: App.State) => getBedbankTotal(state),
  (state: App.State) => getPaymentBalanceTotal(state),
  (
    packageTotal,
    surchargeTotal,
    extraGuestSurchargeTotal,
    insuranceTotal,
    flightTotal,
    experiencesTotal,
    baggageTotal,
    giftCardTotal,
    bedbankTotal,
    paymentBalance,
  ) => (
    packageTotal +
    surchargeTotal +
    extraGuestSurchargeTotal +
    insuranceTotal +
    flightTotal +
    experiencesTotal +
    baggageTotal +
    giftCardTotal +
    paymentBalance +
    bedbankTotal
  ),
)

export const getDiscountTotal = createSelector(
  (state: App.State) => getSubtotal(state),
  (state: App.State) => getFlightsTotal(state),
  (state: App.State) => getExperiencesTotal(state),
  (state: App.State) => getBaggageTotal(state),
  (state: App.State) => getInsuranceTotal(state),
  (state: App.State) => state.cart.promo.discount,
  (state: App.State) => state.cart.promo.type,
  (state: App.State) => state.cart.promo.code,
  (state: App.State) => state.cart.promo.maxDiscount,
  (subtotal, flightsTotal, experiencesTotal, baggageTotal, insuranceTotal, discount, discountType, promoCode, maxDiscount) => {
    // discounts never apply to flights and baggage, so remove their values from the subtotal
    let toDiscountAmount = subtotal - flightsTotal - baggageTotal

    if (promoCode === 'EMPLOYEE20' || promoCode === 'EMPLOYEE5') {
      toDiscountAmount = adjustForDecimal(toDiscountAmount - experiencesTotal - insuranceTotal)
    }

    if (discountType === PROMO_TYPE_FIXED) {
      return Math.min(toDiscountAmount, discount)
    }
    // must be a % based discount then
    const percentageDiscount = Math.round(toDiscountAmount * discount) / 100
    return maxDiscount ? Math.min(percentageDiscount, maxDiscount) : percentageDiscount
  },
)

export const getTotalAfterDiscount = createSelector(
  (state: App.State) => getSubtotal(state),
  (state: App.State) => getDiscountTotal(state),
  (state: App.State) => isChangePackage(state),
  (state: App.State) => getCartChangePackageDiff(state),
  (subtotal, discountTotal, isChangePackage, changePackageTotal) => {
    if (isChangePackage) {
      return Math.max(adjustForDecimal(changePackageTotal))
    }
    const totalAfterDiscount = adjustForDecimal(subtotal - discountTotal)
    return Math.max(totalAfterDiscount, 0)
  },
)

export const getMaximumCreditTotal = createSelector(
  (state: App.State) => getTotalAfterDiscount(state),
  (state: App.State) => state.cart.credit.balance,
  (state: App.State) => state.cart.useLeCredit,
  (state: App.State) => state.auth.account.isSpoofed,
  (state: App.State) => getDepositTotalBeforeCreditApplied(state),
  (state: App.State) => state.cart.rebookingID,
  (state: App.State) => getServiceFee(state),
  (state: App.State) => getTotalAfterDiscount(state),
  (total, creditBalance, useLeCredit, isSpoofed, depositAmount, rebookingID, serviceFee, grandTotal) => {
    let maxCreditTotal

    if (isSpoofed && rebookingID && useLeCredit) {
      const { isEnabled, percentage } = serviceFee
      const serviceFeeTotal = isEnabled ? Number((percentage / 100 * (grandTotal - depositAmount)).toFixed(2)) : 0
      const newDepositAmount = depositAmount + serviceFeeTotal
      maxCreditTotal = creditBalance < newDepositAmount ? creditBalance : newDepositAmount
    } else {
      maxCreditTotal = useLeCredit ? Math.min(total, creditBalance) : 0
    }

    return maxCreditTotal
  },
)

export const getBookingFee = createSelector(
  (state: App.State) => state.cart.flight.journey.bookingFee,
  (bookingFee) => bookingFee > 0 ? bookingFee : 0,
)

export const getBookingServiceFee = createSelector(
  (state: App.State) => state.cart.flight.journey.serviceFee,
  (serviceFee) => serviceFee > 0 ? serviceFee : 0,
)

export const getFlightsCreditFee = createSelector(
  (state: App.State) => state.cart.flight.journey.flightsCreditFee,
  (flightsCreditFee) => flightsCreditFee > 0 ? flightsCreditFee : 0,
)

export const getAtolFee = createSelector(
  (state: App.State) => state.cart.flight.journey.atolFee,
  (atolFee) => atolFee > 0 ? atolFee : 0,
)

export const getGrandTotalExclusiveOfServiceFee = createSelector(
  (state: App.State) => getTotalAfterDiscount(state),
  (state: App.State) => getMaximumCreditTotal(state),
  (state: App.State) => canRebookWithDeposit(state),
  (total, creditTotal, canRebookWithDeposit) => {
    if (canRebookWithDeposit) {
      return adjustForDecimal(total)
    }
    return adjustForDecimal(total - creditTotal)
  },
)

export const getGrandTotalInclusiveOfServiceFee = createSelector(
  (state: App.State) => getTotalAfterDiscount(state),
  (state: App.State) => getMaximumCreditTotal(state),
  (state: App.State) => getServiceFeeExclusiveOfDepositEnabled(state),
  (state: App.State) => canRebookWithDeposit(state),
  (total, creditTotal, serviceFeeTotal, canRebookWithDeposit) => {
    if (canRebookWithDeposit) {
      return adjustForDecimal(total + serviceFeeTotal)
    }

    return adjustForDecimal((total + serviceFeeTotal) - creditTotal)
  },
)

export const getDepositPercentageOnOffer = createSelector(
  (state: App.State) => getDefaultDepositAmountPercentage(state),
  (state: App.State) => state.cart.accommodation.offer,
  (defaultDepositAmountPercentage, offer) => getDepositAmountPercentage(offer, defaultDepositAmountPercentage),
)

export const getDepositTotalBeforeCreditApplied = createSelector(
  (state: App.State) => getSubtotal(state),
  (state: App.State) => getFlightsTotal(state),
  (state: App.State) => getBaggageTotal(state),
  (state: App.State) => getInsuranceTotal(state),
  (state: App.State) => getExperiencesTotal(state),
  getDepositPercentageOnOffer,
  (total, flightsTotal, baggageTotal, insuranceTotal, experiencesTotal, depositAmountPercentage) => {
    const totalWithoutInsuranceAndFlights = total - (insuranceTotal + flightsTotal + experiencesTotal + baggageTotal)

    const depositTotal = adjustForDecimal(totalWithoutInsuranceAndFlights * depositAmountPercentage / 100) + insuranceTotal + flightsTotal + baggageTotal + experiencesTotal

    return depositTotal
  },
)

export const getGrandTotal = createSelector(
  (state: App.State) => getTotalAfterDiscount(state),
  (state: App.State) => getMaximumCreditTotal(state),
  (state: App.State) => isDepositSelected(state),
  (state: App.State) => getServiceFeeTotal(state),
  (state: App.State) => isChangePackage(state),
  (state: App.State) => isInstalmentsSelected(state),
  (total, creditTotal, isDepositSelected, serviceFeeTotal, isChangePackage, isInstalmentsSelected) => {
    if (isChangePackage) {
      return adjustForDecimal(total - creditTotal)
    }
    const inclusiveOfServiceFee = (isDepositSelected || isInstalmentsSelected) ? serviceFeeTotal : 0

    return adjustForDecimal((total + inclusiveOfServiceFee) - creditTotal)
  },
)

export const getDepositTotal = createSelector(
  (state: App.State) => getDepositTotalBeforeCreditApplied(state),
  (state: App.State) => getServiceFeeExclusiveOfDepositEnabled(state),
  (state: App.State) => state.auth.account.isSpoofed,
  (state: App.State) => state.cart.rebookingID,
  (state: App.State) => state.cart.useLeCredit,
  (state: App.State) => state.cart.credit.balance,
  (depositTotalBeforeCreditApplied, serviceFeeTotal, isSpoofed, rebookingID, useLECredits, creditBalance) => {
    let depositTotal = depositTotalBeforeCreditApplied + serviceFeeTotal

    if (isSpoofed && rebookingID && useLECredits) {
      depositTotal = creditBalance < depositTotal ? depositTotal - creditBalance : 0
    }

    return depositTotal
  },
)

export const getPayInInstalmentsTotal = createSelector(
  (state: App.State) => getSubtotal(state),
  (state: App.State) => getFlightsTotal(state),
  (state: App.State) => getBaggageTotal(state),
  (state: App.State) => getInsuranceTotal(state),
  (state: App.State) => getExperiencesTotal(state),
  (total, flightsTotal, baggageTotal, insuranceTotal, experiencesTotal) => {
    return total - (insuranceTotal + flightsTotal + baggageTotal + experiencesTotal)
  },
)

export const getPayInInstalmentsBalance = createSelector(
  (state: App.State) => getPayInInstalmentsTotal(state),
  (state: App.State) => getSubtotal(state),
  (state: App.State) => getSelectedInstalment(state),
  (state: App.State) => getServiceFeeExclusiveOfDepositEnabled(state),
  (state: App.State) => state.auth.account.isSpoofed,
  (state: App.State) => state.cart.rebookingID,
  (state: App.State) => state.cart.useLeCredit,
  (state: App.State) => state.cart.credit.balance,
  (payInInstalmentsTotal, subTotal, selectedInstalment, serviceFeeTotal, isSpoofed, rebookingID, useLECredits, creditBalance) => {
    const instalmentTotal = adjustForDecimal((subTotal - payInInstalmentsTotal) + selectedInstalment.initialPaymentAmount + serviceFeeTotal)

    if (isSpoofed && rebookingID && useLECredits) {
      return creditBalance < instalmentTotal ? adjustForDecimal(instalmentTotal - creditBalance) : 0
    }
    return instalmentTotal
  },
)

export const getServiceFeeExclusiveOfDepositEnabled = createSelector(
  (state: App.State) => getGrandTotalExclusiveOfServiceFee(state),
  (state: App.State) => getDepositTotalBeforeCreditApplied(state),
  (state: App.State) => getServiceFee(state),
  (orderTotal, depositTotal, serviceFee) => {
    const { isEnabled, percentage } = serviceFee
    if (!isEnabled) {
      return 0
    }
    const remainingBalanceToPay = orderTotal - depositTotal
    return Number((percentage / 100 * remainingBalanceToPay).toFixed(2))
  },
)

export const canRebookWithDeposit = createSelector(
  (state: App.State) => state.auth.account.isSpoofed,
  (state: App.State) => state.cart.rebookingID,
  (state: App.State) => state.cart.useLeCredit,
  (state: App.State) => state.cart.credit.balance,
  (state: App.State) => getDepositTotalWithoutServiceFee(state),
  (state: App.State) => getServiceFee(state),
  (state: App.State) => getTotalAfterDiscount(state),
  (isSpoofed, rebookingID, useLECredits, creditBalance, depositTotal, serviceFee, grandTotal) => {
    if (isSpoofed && rebookingID && useLECredits) {
      const { isEnabled, percentage } = serviceFee
      const serviceFeeTotal = isEnabled ? Number((percentage / 100 * (grandTotal - depositTotal)).toFixed(2)) : 0
      const newDepositAmount = depositTotal + serviceFeeTotal
      return creditBalance >= newDepositAmount
    } else {
      return false
    }
  },
)

export const getDepositTotalWithoutServiceFee = createSelector(
  (state: App.State) => getDepositTotalBeforeCreditApplied(state),
  (state: App.State) => state.auth.account.isSpoofed,
  (state: App.State) => state.cart.rebookingID,
  (state: App.State) => state.cart.useLeCredit,
  (state: App.State) => state.cart.credit.balance,
  (depositTotalBeforeCreditApplied, isSpoofed, rebookingID, useLECredits, creditBalance) => {
    let depositTotal = depositTotalBeforeCreditApplied

    if (isSpoofed && rebookingID && useLECredits) {
      depositTotal = creditBalance < depositTotal ? depositTotal - creditBalance : 0
    }

    return depositTotal
  },
)

export const getServiceFeeTotal = createSelector(
  (state: App.State) => getGrandTotalExclusiveOfServiceFee(state),
  (state: App.State) => isFullPaymentSelected(state),
  (state: App.State) => getDepositTotalBeforeCreditApplied(state),
  (state: App.State) => getServiceFee(state),
  (orderTotal, isFullPaymentSelected, depositTotal, serviceFee) => {
    const { isEnabled, percentage } = serviceFee
    if (isFullPaymentSelected || !isEnabled) {
      return 0
    }
    const remainingBalanceToPay = orderTotal - depositTotal
    return Number((percentage / 100 * remainingBalanceToPay).toFixed(2))
  },
)

export const getServiceFeeTotalExclusiveDepositSelected = createSelector(
  (state: App.State) => getGrandTotalExclusiveOfServiceFee(state),
  (state: App.State) => getDepositTotalBeforeCreditApplied(state),
  (state: App.State) => getServiceFee(state),
  (orderTotal, depositTotal, serviceFee) => {
    const { isEnabled, percentage } = serviceFee
    if (!isEnabled) {
      return 0
    }
    const remainingBalanceToPay = orderTotal - depositTotal
    return Number((percentage / 100 * remainingBalanceToPay).toFixed(2))
  },
)

export const getGrandTotalUsingCredit = createSelector(
  (state: App.State) => getTotalAfterDiscount(state),
  (state: App.State) => state.cart.credit.balance,
  (total, credit) => Math.max(adjustForDecimal(total - credit), 0),
)

export const getPackageAmounts = createSelector(
  (state: App.State) => getPackageBaseValue(state),
  (state: App.State) => getPackageBaseTotal(state),
  (state: App.State) => getTaxesAndFeesTotal(state),
  (
    packageBaseValue,
    packageBaseTotal,
    { taxesAndFeesTotal, propertyFeesTotal },
  ) => ({
    baseValue: packageBaseValue,
    packageBaseAmount: packageBaseTotal,
    taxesAndFeesAmount: taxesAndFeesTotal,
    propertyFeesAmount: propertyFeesTotal,
  }),
)

export const getAllAmounts = createSelector(
  (state: App.State) => getPackageAmounts(state),
  (state: App.State) => getSurchargeTotal(state),
  (state: App.State) => getExtraGuestSurchargeTotal(state),
  (state: App.State) => getExperiencesTotal(state),
  (state: App.State) => getBaggageTotal(state),
  (state: App.State) => getSubtotal(state),
  (state: App.State) => getDiscountTotal(state),
  (state: App.State) => getTotalAfterDiscount(state),
  (state: App.State) => getMaximumCreditTotal(state),
  (state: App.State) => getGrandTotalUsingCredit(state),
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => getBedbankTotal(state),
  (
    packageAmounts,
    surchargeTotal,
    extraGuestSurchargeTotal,
    experiencesTotal,
    baggageTotal,
    subtotal,
    discountTotal,
    totalAfterDiscount,
    maxCreditTotal,
    grandTotalWithCredit,
    grandTotal,
    bedbankTotal,
  ) => ({
    value: packageAmounts.baseValue,
    packageAmount: packageAmounts.packageBaseAmount,
    taxesAndFeesAmount: packageAmounts.taxesAndFeesAmount,
    propertyFeesAmount: packageAmounts.propertyFeesAmount,
    surchargeAmount: surchargeTotal,
    extraGuestSurchargeAmount: extraGuestSurchargeTotal,
    experiencesAmount: experiencesTotal,
    baggageAmount: baggageTotal,
    subTotal: subtotal,
    savedAmount: discountTotal,
    totalAfterPromo: totalAfterDiscount,
    creditPaymentAmount: maxCreditTotal,
    grandTotalIfUsingCredit: grandTotalWithCredit,
    grandTotal,
    bedbankTotal,
  } as App.CartAmounts),
)

export const getMergedTaxBreakdown = createSelector(
  (state: App.State) => state.cart.accommodation.occupancies,
  (state: App.State) => state.cart.accommodation.taxBreakdown,
  (
    occupancies,
    taxBreakdown,
  ) => (occupancies || []).reduce((acc, occupancy) => {
    const taxBreakdownPerOccupancy = taxBreakdown[generateOccupancyStringByRoom(occupancy)] ?? []
    taxBreakdownPerOccupancy.forEach(tax => {
      const existingTax = acc.find(taxInAcc => taxInAcc.name === tax.name)
      if (existingTax) {
        existingTax.amount += tax.amount
      } else {
        acc.push({ name: tax.name, amount: tax.amount })
      }
    })
    return acc
  }, []),
)

export const getDepositTotalWithServiceFeeBeforeCreditApplied = createSelector(
  (state: App.State) => getDepositTotalBeforeCreditApplied(state),
  (state: App.State) => getServiceFeeExclusiveOfDepositEnabled(state),
  (depositTotal, serviceFeeTotal) => {
    return depositTotal + serviceFeeTotal
  },
)

export const getDepositBalance = createSelector(
  (state: App.State) => getGrandTotalInclusiveOfServiceFee(state),
  (state: App.State) => getDepositTotal(state),
  (state: App.State) => getGrandTotalBeforeCreditApplied(state),
  (state: App.State) => getDepositTotalBeforeCreditApplied(state),
  (state: App.State) => state.auth.account.isSpoofed,
  (state: App.State) => state.cart.rebookingID,
  (state: App.State) => state.cart.useLeCredit,
  (grandTotal, depositTotal, grandTotalBeforeCreditApplied, depositTotalBeforeCreditApplied, isSpoofed, rebookingID, useLeCredit) => {
    if (isSpoofed && rebookingID && useLeCredit) {
      return grandTotalBeforeCreditApplied - depositTotalBeforeCreditApplied
    }
    return grandTotal - depositTotal
  },
)

export const getGrandTotalBeforeCreditApplied = createSelector(
  (state: App.State) => getSubtotal(state),
  (state: App.State) => cartHasFlights(state),
  (state: App.State) => state.cart.flight.journey.cost || state.cart.flight.basePriceSnapshot,
  // The subtotal will have the flight price when the journey is fully populated
  (subTotal, cartIncludesFlights, flightPrice) => cartIncludesFlights ? subTotal : (subTotal + flightPrice),
)
